/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import EditIcon from '@material-ui/icons/Edit';
import {
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  TableBody,
  IconButton,
  TablePagination,
  Typography,
} from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { Timestamp } from 'models/firebase';
import { timestampToString } from 'helpers/date';
import { Message, StorageKey, NavigationBack } from 'models';
import { EDIT_MESSAGE } from 'constants/routes';
import { HeadCell } from 'models/tables';
import Thumbnail from 'components/tables/Thumbnail';
import TableLoading from 'components/loading/TableLoading';
import TableHead from 'components/tables/TableHead';
import useMessages from 'hooks/useMessages';
import useLocalStorage from 'hooks/useLocalStorage';
import { EMPTY_MESSAGE } from 'constants/forms';
import useStyles from './style';

const headCells: HeadCell[] = [
  { id: 'image', label: 'Image' },
  { id: 'text', label: 'Text' },
  { id: 'date', label: 'Date' },
  { id: 'edit', label: 'Edit' },
];

const MessagesTable: React.FC<{}> = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { getAll, messages, loading } = useMessages();
  const { get, set } = useLocalStorage();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Local Storage.
  const getRowsFromLocalStorage = async (): Promise<void> => {
    const rows = await get<number>(StorageKey.MessagesTableRPP);
    if (rows) setRowsPerPage(rows);
  };

  const getPageFromLocalStorage = async (): Promise<void> => {
    const params = location.state as NavigationBack;
    let currentPage = 0;
    if (history.action === 'PUSH' && params?.goBack) {
      const newPageNumber = await get<number>(StorageKey.MessagesTablePage);
      if (newPageNumber) {
        currentPage = newPageNumber;
      }
    }
    setPage(currentPage);
  };

  const setToLocalStorage = async (rows: number): Promise<void> => {
    await set(StorageKey.MessagesTableRPP, rows);
  };

  // Hanlders.
  const handleChangePage = async (
    _: unknown,
    newPage: number,
  ): Promise<void> => {
    setPage(newPage);
    await set(StorageKey.MessagesTablePage, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const rowsNumber = parseInt(event.target.value, 10);
    setToLocalStorage(rowsNumber);
    setRowsPerPage(rowsNumber);
    setPage(0);
  };

  const handleEdit = (message: Message): void => {
    history.push(EDIT_MESSAGE, { message });
  };

  // Efects.
  useEffect(() => {
    getRowsFromLocalStorage();
    getPageFromLocalStorage();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getAll();
  }, []);

  return (
    <div className={classes.container}>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead cells={headCells} />
            <TableBody>
              {loading && <TableLoading colSpan={headCells.length} />}
              {!loading &&
                messages
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: Message) => (
                    <TableRow key={row.id}>
                      <TableCell align='left'>
                        <Thumbnail src={row.imageURL} />
                      </TableCell>
                      <TableCell align='left'>
                        {row.text || row.text !== '' ? (
                          <Typography variant='body2'>{row.text}</Typography>
                        ) : (
                          <Typography
                            variant='body2'
                            className={classes.emptyText}
                          >
                            {EMPTY_MESSAGE}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align='left'>
                        {timestampToString(row.date as Timestamp)}
                      </TableCell>
                      <TableCell align='left'>
                        <IconButton
                          aria-label='edit'
                          onClick={(): void => handleEdit(row)}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && (
          <TablePagination
            page={page}
            component='div'
            count={messages.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </div>
  );
};

export default MessagesTable;
